import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { platform } from "@/platform";
import { t } from "@/i18n/ko";
import { goPath } from "@/app/deepLink";
import { STAGE_LINKS } from "@/app/appLinks";
import { EmptyState } from "@/components/EmptyState";

/**
 * `/go?plot=<key>` — 앱 링크 경로(AC0-261). 앱이 설치돼 있으면 OS 가 앱으로 열어(App Links·Universal Links) 이 페이지는 웹에서 거의 보이지 않는다.
 * 웹: iOS 브라우저는 App Store(VITE_APP_STORE_URL), 그 밖은 Play(VITE_STORE_URL). 해당 URL 이 없으면 플롯 프로필(plot 없으면 /home) — iOS 를 Play 로 보내지 않는다(10/28).
 * Play 로 갈 때는 `referrer=plot=<key>&utm_*` 를 붙인다(AC0-360 디퍼드 딥링크: 설치 뒤 첫 부팅이 app/bootstrap.ts 에서 Install Referrer 로 같은 플롯을 연다).
 * Android 브라우저는 자동 이동 대신 버튼: JS 리다이렉트는 사용자 제스처가 없어 Chrome 이 Play 앱 대신 Play 웹 페이지를 띄우고, 웹에서 누른 설치는
 * 리퍼러 없이(`utm_source=(not set)`) 진행된다(11/4 실기기 확인). 탭으로 `intent://…;scheme=market` 을 열어 Play 앱에 리퍼러를 그대로 넘긴다.
 * 앱 안(라우터로 직접 온 경우): 플롯 프로필(나머지 쿼리 유지).
 */
/** iOS·iPadOS 브라우저(iPadOS 데스크톱 모드는 MacIntel + 터치) */
export const isIosBrowser = (nav: Pick<Navigator, "userAgent" | "platform" | "maxTouchPoints"> = navigator): boolean =>
  /iPhone|iPad|iPod/.test(nav.userAgent) || (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);

/** Android 브라우저(태블릿 포함). */
export const isAndroidBrowser = (nav: Pick<Navigator, "userAgent"> = navigator): boolean => /Android/i.test(nav.userAgent);

export const storeUrlFor = (ios: boolean, env: { VITE_STORE_URL?: string; VITE_APP_STORE_URL?: string } = import.meta.env): string | undefined =>
  (ios ? env.VITE_APP_STORE_URL : env.VITE_STORE_URL) || undefined;

/**
 * Play Install Referrer 에 실을 값. GA 캠페인 형식을 앞에 두고(`utm_source`·`utm_medium` 은 없으면 기본값 hamlak·go, 들어온 utm_* 은 그대로)
 * `plot=<key>` 를 맨 뒤에 붙인다. 그 외 쿼리는 버린다. Play 가 리퍼러를 캠페인으로 파싱하지 못하면 통째로 `(not set)` 으로 바꾼다는 보고가 있어(11/4) 표준 형식을 지킨다.
 */
export const playReferrer = (search: URLSearchParams): string => {
  const out = new URLSearchParams({ utm_source: search.get("utm_source")?.trim() || "hamlak", utm_medium: search.get("utm_medium")?.trim() || "go" });
  for (const [k, v] of search) if (k.startsWith("utm_") && !out.has(k) && v.trim()) out.set(k, v.trim());
  const plot = search.get("plot")?.trim();
  if (plot) out.set("plot", plot);
  return out.toString();
};

/** 스토어 URL 에 Play 리퍼러를 붙인다(Android 만). URL 이 아니면 그대로. */
export const withPlayReferrer = (storeUrl: string, referrer: string | null): string => {
  if (!referrer) return storeUrl;
  try {
    const u = new URL(storeUrl);
    u.searchParams.set("referrer", referrer);
    return u.toString();
  } catch {
    return storeUrl;
  }
};

/**
 * Chrome Android 의 intent URL: Play 앱(com.android.vending)을 `market://details?id&referrer` 로 직접 연다. Play 앱이 없으면 browser_fallback_url(https, 리퍼러 포함).
 * 사용자 탭(제스처) 안에서 열어야 Chrome 이 외부 앱 실행을 허용한다.
 */
export const playIntentUrl = (packageName: string, referrer: string | null, fallbackUrl: string): string => {
  const q = new URLSearchParams({ id: packageName });
  if (referrer) q.set("referrer", referrer);
  return `intent://details?${q.toString()}#Intent;scheme=market;package=com.android.vending;S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
};

const androidPackage = () => STAGE_LINKS[import.meta.env.VITE_STAGE === "prod" ? "prod" : "dev"].androidPackage;

/** 웹 /go 의 행선지: Android 브라우저는 탭으로 여는 Play intent(`tap`), 그 밖은 자동 이동(`redirect`), 스토어 URL 이 없으면 null(앱 안 라우팅). */
export function webDestination(params: URLSearchParams, nav: Navigator = navigator): { tap: string } | { redirect: string } | null {
  const ios = isIosBrowser(nav);
  const store = storeUrlFor(ios);
  if (!store) return null;
  if (ios) return { redirect: store };
  const referrer = playReferrer(params);
  const https = withPlayReferrer(store, referrer);
  return isAndroidBrowser(nav) ? { tap: playIntentUrl(androidPackage(), referrer, https) } : { redirect: https };
}

export function GoPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dest = useMemo(() => (platform.clientKind === "web" ? webDestination(params) : null), [params]);
  const playUrl = dest && "tap" in dest ? dest.tap : null;
  useEffect(() => {
    if (playUrl) return;
    if (dest && "redirect" in dest) {
      window.location.replace(dest.redirect);
      return;
    }
    navigate(goPath(params), { replace: true });
  }, [dest, playUrl, params, navigate]);
  if (playUrl) {
    return <EmptyState message={t("go.installPrompt")} action={{ label: t("go.openPlay"), onClick: () => window.location.assign(playUrl) }} />;
  }
  return <div className="page-placeholder">{t("page.go")}</div>;
}
